import { useCallback, useMemo } from 'react';
import * as R from 'remeda';
import { UniqueIdentifier } from '@dnd-kit/core';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { RouteChildrenProps, useHistory } from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import { getEtag, WithEtag } from '@rossum/api-client';
import { ElisClientError, HttpError } from '@rossum/api-client/errors';
import {
  isSchemaDatapoint,
  isSchemaDatapointButton,
  isSchemaSection,
  Schema,
} from '@rossum/api-client/schemas';
import { IconEdit, IconTrash } from '@rossum/ui/icons/tabler';
import { Button, Stack, Typography } from '@rossum/ui/material';
import { usePatchSchema } from '../../../../business/schema/usePatchSchema';
import {
  throwError,
  throwInfo,
} from '../../../../redux/modules/messages/actions';
import { getFlattenSchemaFields } from '../../../schemas/getFlattenSchemaFields';
import { useSectionsAccordions } from '../../hooks/useSectionsAccordions';
import { FieldsList, FieldsListProps } from '../components/FieldsList';
import { SchemaFieldTile } from '../components/SchemaFieldTile';
import { SectionsReordering } from '../components/SectionsReordering';
import { toSchemaPatchPayload } from '../form-model/transformations/toSchema';
import { SchemaActionHandler } from '../form-model/transformations/toSchema.utils';
import {
  buildSchemaEditorPath,
  getDnDItems,
  isQuickActionDisabled,
  resolveCanExport,
} from '../helpers';
import { useReorderingSectionsState } from '../hooks/useReorderingSectionsState';

type FieldsPageProps = RouteChildrenProps & {
  schema: WithEtag<Schema>;
  schemaIsFetching: boolean;
};

export const FieldsPage = ({ schema, schemaIsFetching }: FieldsPageProps) => {
  const history = useHistory();
  const intl = useIntl();

  const sectionsAccordionsProps = useSectionsAccordions({
    schemaId: `${schema.id}`,
  });

  const flattenSchemaFields = useMemo(
    () => getFlattenSchemaFields(schema.content || []),
    [schema.content]
  );

  const { mutateAsync, isLoading } = usePatchSchema({
    etag: getEtag(schema),
  });

  const dispatch = useDispatch();

  const schemaUpdateDisabled = schemaIsFetching || isLoading;

  const executeSchemaAction: SchemaActionHandler = useCallback(
    options => {
      const payload = toSchemaPatchPayload(schema, options);

      if (payload && !schemaUpdateDisabled) {
        mutateAsync({ id: payload.id, payload })
          .then(() => {
            dispatch(throwInfo('schemaUpdated'));
          })
          .catch((_error: ElisClientError) => {
            if (_error instanceof HttpError && _error.code === 412) {
              dispatch(throwError('schemaUpdateConflict'));
            } else {
              dispatch(throwError('schemaUpdateFailed'));
            }
          });
      }
    },
    [schemaUpdateDisabled, dispatch, mutateAsync, schema]
  );

  const onAddSection = useCallback(() => {
    const temporarySchemaSectionId = uuidv4();

    history.push({
      pathname: `${history.location.pathname}/${temporarySchemaSectionId}`,
      state: { backLink: history.location.pathname },
    });
  }, [history]);

  const renderFieldItem: FieldsListProps['renderFieldItem'] = useCallback(
    (fieldId, parentId) => {
      const item = flattenSchemaFields[fieldId]?.field;

      if (!item || isSchemaSection(item)) return null;

      const canExportValue = resolveCanExport(item);

      return (
        <SchemaFieldTile
          field={item}
          parentId={parentId}
          isFaded={!!item.hidden}
          quickActions={[
            {
              key: 'hidden',
              value: !item.hidden,
              disabled: schemaUpdateDisabled,
              onClick: () => {
                if (parentId) {
                  executeSchemaAction({
                    op: 'quick-action',
                    parentId,
                    items: {
                      [item.id]: {
                        prop: 'hidden',
                        value: !item.hidden,
                      },
                    },
                  });
                }
              },
            },
            {
              key: 'exported',
              value: canExportValue,
              disabled: isQuickActionDisabled(item),
              onClick: () => {
                if (parentId) {
                  executeSchemaAction({
                    op: 'quick-action',
                    parentId,
                    items: {
                      [item.id]: {
                        prop: 'canExport',
                        value: !canExportValue,
                      },
                    },
                  });
                }
              },
            },
            {
              key: 'required',
              value:
                isSchemaDatapoint(item) && !isSchemaDatapointButton(item)
                  ? !!item.constraints?.required
                  : false,
              disabled: isQuickActionDisabled(item),
              onClick: () => {
                if (parentId) {
                  executeSchemaAction({
                    op: 'quick-action',
                    parentId,
                    items: {
                      [item.id]: {
                        prop: 'required',
                        value:
                          isSchemaDatapoint(item) &&
                          !isSchemaDatapointButton(item)
                            ? !item.constraints?.required
                            : false,
                      },
                    },
                  });
                }
              },
            },
          ]}
          contextActions={[
            {
              key: 'edit-json',
              label: intl.formatMessage({
                id: 'features.queueSettings.fields.contextActions.editJson',
              }),
              icon: IconEdit,
              to: {
                pathname: buildSchemaEditorPath(history.location.pathname),
                search: `?id=${item.id}`,
                state: { backLink: history.location.pathname },
              },
            },
            {
              key: 'delete',
              label: intl.formatMessage({
                id: 'features.queueSettings.fields.contextActions.deleteItem',
              }),
              icon: IconTrash,
              onClick: () => {
                if (parentId) {
                  executeSchemaAction({
                    op: 'delete',
                    id: item.id,
                  });
                }
              },
            },
          ]}
        />
      );
    },
    [
      flattenSchemaFields,
      schemaUpdateDisabled,
      intl,
      executeSchemaAction,
      history,
    ]
  );

  const items = useMemo(
    () => (schema.content ? getDnDItems(schema.content) : {}),
    [schema.content]
  );

  const sectionsLabels = useMemo(
    () =>
      schema.content
        ? schema.content.reduce<Record<string, string>>((acc, section) => {
            return {
              ...acc,
              [section.id]: section.label,
            };
          }, {})
        : {},
    [schema.content]
  );

  // generates path to new field page for a section
  const newFieldPathForSection = useCallback(
    (sectionId: UniqueIdentifier) => {
      return `${history.location.pathname}/${sectionId}/${uuidv4()}`;
    },
    [history.location.pathname]
  );

  // generates path to detail page for a section
  const detailPathForSection = useCallback(
    (sectionId: UniqueIdentifier) => {
      return `${history.location.pathname}/${sectionId}`;
    },
    [history.location.pathname]
  );

  const contextActionsForSection = useCallback(
    (sectionId: UniqueIdentifier) => {
      return [
        {
          key: 'delete',
          section: sectionId,
          label: intl.formatMessage({
            id: 'features.queueSettings.fields.contextActions.deleteSection',
          }),
          icon: IconTrash,
          onClick: () =>
            executeSchemaAction({ op: 'delete', id: String(sectionId) }),
        },
      ];
    },
    [executeSchemaAction, intl]
  );

  // generates bulk actions for a section
  // TODO: Simplify this
  const bulkActionsForSection = useCallback(
    (sectionId: UniqueIdentifier) => {
      const itemsInSection = items[sectionId];

      if (!itemsInSection) return [];

      return [
        {
          key: 'hidden',
          value: itemsInSection.every(
            itemId => !flattenSchemaFields[itemId]?.field.hidden
          ),
          onClick: () => {
            executeSchemaAction({
              op: 'quick-action',
              parentId: sectionId,
              items: R.fromEntries(
                itemsInSection.map(
                  item =>
                    [
                      item,
                      {
                        prop: 'hidden' as const,
                        value: itemsInSection.every(
                          itemId => !flattenSchemaFields[itemId]?.field.hidden
                        ),
                      },
                    ] as const
                )
              ),
            });
          },
        },
        {
          key: 'exported',
          value: itemsInSection
            .flatMap(itemId => {
              const item = flattenSchemaFields[itemId]?.field;

              return isSchemaDatapoint(item) && !isSchemaDatapointButton(item)
                ? item
                : [];
            })
            .every(item => item.canExport),
          onClick: () => {
            executeSchemaAction({
              op: 'quick-action',
              parentId: sectionId,
              items: R.fromEntries(
                itemsInSection.map(item => [
                  item,
                  {
                    id: item,
                    prop: 'canExport' as const,
                    value: !itemsInSection
                      .flatMap(itemId => {
                        const schemaField = flattenSchemaFields[itemId]?.field;

                        return isSchemaDatapoint(schemaField) &&
                          !isSchemaDatapointButton(schemaField)
                          ? schemaField
                          : [];
                      })
                      .every(schemaItem => schemaItem.canExport),
                  },
                ])
              ),
            });
          },
        },
        {
          key: 'required',
          value: itemsInSection
            .flatMap(itemId => {
              const item = flattenSchemaFields[itemId]?.field;

              return isSchemaDatapoint(item) && !isSchemaDatapointButton(item)
                ? item
                : [];
            })
            .every(item => item.constraints?.required),
          onClick: () => {
            executeSchemaAction({
              op: 'quick-action',
              parentId: sectionId,
              items: R.fromEntries(
                itemsInSection.map(item => [
                  item,
                  {
                    id: item,
                    prop: 'required' as const,
                    value: !itemsInSection
                      .flatMap(itemId => {
                        const schemaField = flattenSchemaFields[itemId]?.field;

                        return isSchemaDatapoint(schemaField) &&
                          !isSchemaDatapointButton(schemaField)
                          ? schemaField
                          : [];
                      })
                      .every(schemaItem => schemaItem.constraints?.required),
                  },
                ])
              ),
            });
          },
        },
      ] as const;
    },
    [flattenSchemaFields, items, executeSchemaAction]
  );

  const handleFieldsReorder: FieldsListProps['onItemsReorder'] = useCallback(
    (from, to) => {
      executeSchemaAction({
        op: 'move',
        from,
        to,
      });
    },
    [executeSchemaAction]
  );

  const { setExitingState, shouldCollapseSections, reorderingVisible } =
    useReorderingSectionsState();

  if (!schema.content || schema.content.length === 0) {
    return (
      <Stack alignItems="center" px={4} py={8} spacing={2}>
        <Typography variant="h5" color="text.secondary">
          {intl.formatMessage({
            id: 'features.queueSettings.fields.emptyState.title',
          })}
        </Typography>

        <Typography component="div" color="text.secondary" textAlign="center">
          {intl.formatMessage({
            id: 'features.queueSettings.fields.emptyState.description',
          })}
        </Typography>

        <Button
          key="add-section"
          variant="contained"
          onClick={onAddSection}
          data-cy="add-section-empty-schema-button"
        >
          {intl.formatMessage({
            id: 'features.queueSettings.actions.addSection',
          })}
        </Button>
      </Stack>
    );
  }

  return (
    <Stack p={4} spacing={4}>
      {reorderingVisible ? (
        <SectionsReordering
          sections={schema.content}
          onSectionsReorder={handleFieldsReorder}
        />
      ) : (
        <FieldsList
          items={items}
          renderFieldItem={renderFieldItem}
          sectionsLabels={sectionsLabels}
          onItemsReorder={handleFieldsReorder}
          newFieldPathForSection={newFieldPathForSection}
          detailPathForSection={detailPathForSection}
          bulkActionsForSection={bulkActionsForSection}
          contextActionsForSection={contextActionsForSection}
          setExiting={setExitingState}
          sectionsAccordionsProps={
            shouldCollapseSections
              ? {
                  sectionsAccordionsState: R.mapValues(items, () => false),
                  toggleSectionAccordion: () => {},
                }
              : sectionsAccordionsProps
          }
        />
      )}
    </Stack>
  );
};
